import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Flex,
  Form,
  InputNumber,
  Spin,
  Typography,
  message,
} from "antd";
import { Auth } from "aws-amplify";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import {
  useGetRevenueTargetQuery,
  useUpdateRevenueTargetMutation,
} from "../../../api/admin/sales/RevenueTargetsApi";

const { Title, Text } = Typography;

function quarterKey(value: Dayjs): string {
  return `${value.year()}-Q${Math.floor(value.month() / 3) + 1}`;
}

function formatUpdatedAt(value: string): string {
  return `${new Date(value).toLocaleString("en-SG", {
    timeZone: "Asia/Singapore",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })} SGT (UTC+8)`;
}

export default function RevenueSettingsPage() {
  const [quarter, setQuarter] = useState(dayjs());
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const apiQuarter = quarterKey(quarter);
  const { currentData: data, isLoading, isError } = useGetRevenueTargetQuery(apiQuarter, {
    skip: isAdmin !== true,
  });
  const [updateTarget, { isLoading: isSaving }] = useUpdateRevenueTargetMutation();

  useEffect(() => {
    Auth.currentUserInfo()
      .then(user => setIsAdmin(user?.attributes?.profile === "admin"))
      .catch(() => setIsAdmin(false));
  }, []);

  useEffect(() => {
    setAmount(data?.target ? data.target.targetAmountCents / 100 : null);
  }, [data]);

  async function saveTarget() {
    if (amount === null || amount <= 0) {
      message.error("Enter a target amount greater than zero");
      return;
    }

    try {
      await updateTarget({
        quarter: apiQuarter,
        targetAmountCents: Math.round(amount * 100),
      }).unwrap();
      message.success("Revenue target saved");
    } catch {
      message.error("Unable to save the revenue target");
    }
  }

  if (isAdmin === null) return <Spin />;
  if (!isAdmin) {
    return <Alert
      type="error"
      showIcon
      message="Administrator access is required"
    />;
  }

  return <Flex gap={16} vertical>
    <div>
      <Title level={3} style={{ margin: 0 }}>Revenue Settings</Title>
      <Text type="secondary">Set quarterly targets for the PH revenue dashboard</Text>
    </div>

    {isError && <Alert type="error" showIcon message="Unable to load the revenue target" />}

    <Card title="PH Quarterly Target">
      <Spin spinning={isLoading}>
        <Form layout="vertical" style={{ maxWidth: 480 }}>
          <Form.Item label="Quarter">
            <DatePicker
              picker="quarter"
              value={quarter}
              onChange={value => value && setQuarter(value)}
              allowClear={false}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item label="Target amount (PHP)">
            <InputNumber
              value={amount}
              onChange={setAmount}
              min={0.01}
              precision={2}
              prefix="₱"
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Button type="primary" loading={isSaving} onClick={saveTarget}>
            Save Target
          </Button>
        </Form>
      </Spin>
    </Card>

    {data?.target && <Card title="Last Update">
      <Descriptions column={1} size="small">
        <Descriptions.Item label="Updated by">{data.target.updatedBy}</Descriptions.Item>
        <Descriptions.Item label="Updated at">
          {formatUpdatedAt(data.target.updatedAt)}
        </Descriptions.Item>
      </Descriptions>
    </Card>}
  </Flex>;
}
